import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Card, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';

interface ComparisonSankeyChartProps {
  version1Label: string;
  version2Label: string;
  fixedCount: number;
  unfixedCount: number;
  newCount: number;
}

const FIXED_NODE = 'Fixed';
const UNFIXED_NODE = 'Unfixed';
const NEW_NODE = 'New';

/**
 * Sankey diagram showing how CVEs flow from version1 to version2:
 * version1's CVEs either get fixed (dead-end, don't carry over) or remain
 * unfixed and carry over into version2, which also picks up newly
 * introduced CVEs. Node `depth` is set explicitly so "New" (which has no
 * incoming link) still renders in the middle column instead of being
 * placed in the leftmost column by echarts' automatic layering.
 */
const ComparisonSankeyChart: React.FC<ComparisonSankeyChartProps> = ({
  version1Label,
  version2Label,
  fixedCount,
  unfixedCount,
  newCount,
}) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const legendItems = [
    { label: FIXED_NODE, count: fixedCount, color: theme.palette.success.main },
    { label: UNFIXED_NODE, count: unfixedCount, color: theme.palette.error.main },
    { label: NEW_NODE, count: newCount, color: theme.palette.warning.main },
  ];

  useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const textColor = theme.palette.mode === 'dark' ? '#fff' : '#222';

    // Only draw links that actually carry CVEs - a 0-count link would
    // otherwise still render (and show a misleading rounded-up tooltip value).
    const links = [
      { source: version1Label, target: FIXED_NODE, value: fixedCount },
      { source: version1Label, target: UNFIXED_NODE, value: unfixedCount },
      { source: UNFIXED_NODE, target: version2Label, value: unfixedCount },
      { source: NEW_NODE, target: version2Label, value: newCount },
    ].filter((link) => link.value > 0);

    // Drop any node left with no links as a result (e.g. "New" when
    // newCount is 0), since an isolated node has nothing to connect to.
    const connectedNodes = new Set(
      links.flatMap((link) => [link.source, link.target])
    );
    const data = [
      { name: version1Label, depth: 0, itemStyle: { color: theme.palette.grey[500] } },
      { name: FIXED_NODE, depth: 1, itemStyle: { color: theme.palette.success.main } },
      { name: UNFIXED_NODE, depth: 1, itemStyle: { color: theme.palette.error.main } },
      { name: NEW_NODE, depth: 1, itemStyle: { color: theme.palette.warning.main } },
      { name: version2Label, depth: 2, itemStyle: { color: theme.palette.grey[500] } },
    ].filter((node) => connectedNodes.has(node.name));

    if (links.length === 0) {
      // No CVE differences at all between the two versions - an empty
      // sankey series has nothing to lay out, so just clear the canvas.
      chartInstance.current.clear();
      return;
    }

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
      },
      series: [
        {
          type: 'sankey',
          emphasis: { focus: 'adjacency' },
          nodeAlign: 'justify',
          // Disable the link-crossing-minimization pass so nodes within a
          // column keep the fixed order they're listed in `data` below,
          // instead of being re-sorted by flow size on every render.
          layoutIterations: 0,
          lineStyle: { color: 'gradient', curveness: 0.5 },
          label: { color: textColor, fontWeight: 'bold' },
          data,
          links,
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [theme, version1Label, version2Label, fixedCount, unfixedCount, newCount]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <Card sx={{ width: '100%' }}>
      <Stack
        direction="row"
        spacing={3}
        sx={{ p: 2, justifyContent: 'center' }}
      >
        {legendItems.map(({ label, count, color }) => (
          <Stack key={label} direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: color,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {label}: {count}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ position: 'relative', height: 450, width: '100%' }}>
        <Box ref={chartRef} sx={{ height: '100%', width: '100%' }} />
        {fixedCount + unfixedCount + newCount === 0 && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6">No CVE differences to display</Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default ComparisonSankeyChart;
